import {
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  forwardRef,
  Input,
  QueryList
} from "@angular/core";
import {BehaviorSubject, Observable} from "rxjs";

export class AbstractNDropdownMenuItemComponent {
  getDropdownMenuItem(): IDropdownMenuItem {
    return null;
  }
}

@Component({
  selector: "n-dropdown-menu",
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `:host {
      display: none
    }`
  ],
  template: ``
})
export class NDropdownMenuComponent {

  @Input()
  wide: boolean;
  @Input()
  mobileView: boolean;

  private focusedItemIndex$ = new BehaviorSubject<number>(-1);
  private menuItems$ = new BehaviorSubject<IDropdownMenuItem[]>([]);

  @ContentChildren(forwardRef(() => AbstractNDropdownMenuItemComponent))
  set dropdownItemComponents(queryList: QueryList<AbstractNDropdownMenuItemComponent>) {
    const updateMenuItems = (itemComponents) => {
      const menuItems: IDropdownMenuItem[] = itemComponents.map(item => item.getDropdownMenuItem());
      this.menuItems$.next(menuItems);
    };

    queryList.changes.subscribe(updateMenuItems);
    updateMenuItems(queryList);
  }

  get focusedIndex$(): Observable<number> {
    return this.focusedItemIndex$;
  }

  get focusedIndexSnapshot(): number {
    return this.focusedItemIndex$.value;
  }

  getItems(): Observable<IDropdownMenuItem[]> {
    return this.menuItems$.asObservable();
  }

  getItemsSnapshot(): IDropdownMenuItem[] {
    return this.menuItems$.value;
  }

  // noinspection JSMethodCanBeStatic
  select(item: IDropdownMenuItem) {
    if (item) {
      item.select.emit();
    }
  }

  selectFocused() {
    const focusedItemIndex = this.focusedItemIndex$.value;
    if (focusedItemIndex >= 0) {
      const item = this.menuItems$.value[focusedItemIndex];
      if (item) {
        this.select(item);
      }
    }
  }

  focusPrev() {
    const focusedItemIndex = this.focusedItemIndex$.value;
    if (focusedItemIndex === 0) {
      return;
    }
    const menuItems = this.menuItems$.value;
    this.setFocusedItemIndex(this.getPrevItem(menuItems, focusedItemIndex));
  }

  focusNext() {
    const focusedItemIndex = this.focusedItemIndex$.value,
      menuItems = this.menuItems$.value;
    this.setFocusedItemIndex(this.getNextItem(menuItems, focusedItemIndex));
  }

  setFocusedItemIndex(index: number) {
    this.focusedItemIndex$.next(index);
  }

  focusFirst() {
    const items = this.menuItems$.value;
    this.setFocusedItemIndex(this.getNextItem(items, -1));
  }

  focusLast() {
    const items = this.menuItems$.value;
    this.setFocusedItemIndex(this.getPrevItem(items, items.length));
  }

  private getNextItem(menuItems: IDropdownMenuItem[], focusedItemIndex: number): number {
    if (focusedItemIndex === menuItems.length - 1) {
      return focusedItemIndex;
    }
    if (menuItems[focusedItemIndex + 1].divider || menuItems[focusedItemIndex + 1].disabledSnapshot()) {
      return this.getNextItem(menuItems, focusedItemIndex + 1);
    } else {
      return focusedItemIndex + 1;
    }
  }

  private getPrevItem(menuItems: IDropdownMenuItem[], focusedItemIndex: number): number {
    if (focusedItemIndex === 0) {
      return focusedItemIndex;
    }
    if (focusedItemIndex === -1) {
      return this.getPrevItem(menuItems, menuItems.length);
    }
    if (menuItems[focusedItemIndex - 1].divider || menuItems[focusedItemIndex - 1].disabledSnapshot()) {
      return this.getPrevItem(menuItems, focusedItemIndex - 1);
    } else {
      return focusedItemIndex - 1;
    }
  }
}

export interface IDropdownMenuItem {
  divider?: boolean;
  heading: Observable<string>;
  description?: Observable<string>;
  counter?: Observable<string>;
  disabled: Observable<boolean>;
  iconId?: Observable<string>;
  iconColor?: Observable<string>;
  iconSize?: Observable<number>;
  url?: Observable<string>;
  id: any;
  select: EventEmitter<any>;
  tid?: string;

  headingSnapshot(): string;

  disabledSnapshot(): boolean;
}
